/* eslint-disable no-useless-escape */
import { Avatar, Box, Button, CircularProgress, Dialog } from '@mui/material';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useIdleTimer } from 'react-idle-timer';
import { Outlet } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { useMenuSelect } from '../../hooks/useMenuSelect';

import { Header } from '../../components/Header';
import { InputPassword } from '../../components/InputPassword';

import {
  Tokens,
  fastLoginRequest,
  getTokensLocalStorage,
  setTokensLocalStorage,
  signOutRequest,
} from '../../services';
import api from '../../services/api';

import {
  Container,
  Content,
  DialogTextStyles,
  DialogTitleStyles,
  Divider,
  Page,
} from './styles';

import useAuthStore from '../../store/auth';

import { stringAvatar } from '../../utils/avatarGenerator';
import useLoadingStore from '../../store/loading';
import useCompanyStore from '../../store/companies';
import { LoadingBackdrop } from '../../components/LoadingBackdrop';
import { TurnstileWidget } from '../../components/TurnstileWidget';

const hasCaptcha = process.env.REACT_APP_TURNSTILE_ENABLED || 'false';

export function Layout() {
  const user = useAuthStore(state => state.user);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const setCompanies = useCompanyStore(state => state.setCompanies);
  const signOut = useAuthStore(state => state.signOut);
  const loading = useLoadingStore(state => state.loading);
  const tokensTest = getTokensLocalStorage() as Tokens;

  const [isModal, setIsModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const turnstileRef = useRef<HTMLDivElement | null>(null);

  const { getUrlToSetMenu } = useMenuSelect();

  const TWENTY_MINUTES_IN_SECONDS = 1200000;

  function stopPrntScr() {
    let inpFld = document.createElement('input');
    inpFld.setAttribute('value', '.');
    inpFld.setAttribute('width', '0');
    inpFld.style.height = '0px';
    inpFld.style.width = '0px';
    inpFld.style.border = '0px';
    document.body.appendChild(inpFld);
    inpFld.select();
    document.execCommand('copy');
    inpFld.remove();
  }
  document.addEventListener('keyup', function (e) {
    let keyCode = e.keyCode ? e.keyCode : e.which;
    if (keyCode === 44) {
      stopPrntScr();
    }
  });

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      password: Yup.string()
        .min(8, 'Mínimo de 8 Dígitos')
        .required('O campo senha é obrigatório'),
    });
  }, []);

  const { control, handleSubmit, watch } = useForm({
    resolver: yupResolver(formValidation()),
  });

  const tokenValue = watch('token');
  const btnIsDisabledByCaptcha =
    hasCaptcha === 'true' && tokenValue === undefined;

  const onIdle = () => {
    setTokensLocalStorage({ access_token: '', refresh_token: '' });
    setIsModal(true);
  };

  useIdleTimer({
    onIdle,
    timeout: TWENTY_MINUTES_IN_SECONDS,
  });

  const reAuth = useCallback(
    async (formData: any) => {
      setLoadingModal(true);

      try {
        const response = await fastLoginRequest({
          email: user?.email as string,
          password: formData.password,
        });

        if (response?.status === 200) {
          const { access_token, refresh_token, companies } = response.data;

          api().defaults.headers.common.Authorization = `Bearer ${access_token}`;

          const tokens = {
            access_token,
            refresh_token,
          };

          setCompanies(companies);
          toast.success('Autenticado com sucesso, você esta sendo reconectado');
          setTimeout(
            function () {
              setIsModal(false);
              setLoadingModal(false);
              setTokensLocalStorage(tokens);
              window.location.reload();
            },

            2500,
          );
        } else {
          toast.error(
            'Os dados inseridos estão incorretos, por segurança você esta sendo desconectado',
          );

          setTimeout(function () {
            signOut();
          }, 2500);
        }
      } catch (error) {
        toast.error('Erro ao autenticar tente novamente mais tarde');
      } finally {
        setLoadingModal(false);
      }
    },
    [signOut, user?.email, setCompanies],
  );

  const handleSignOut = useCallback(() => {
    signOut();
    signOutRequest();
  }, [signOut]);

  useEffect(() => {
    if (
      !tokensTest ||
      tokensTest?.access_token === '' ||
      tokensTest?.access_token === null
    ) {
      setTimeout(() => {
        toast.dismiss();
        handleSignOut();
      }, 500);
    }
  }, [handleSignOut, tokensTest]);

  useEffect(() => {
    if (window.location.href) {
      getUrlToSetMenu();
    }
  }, [window.location.href]);

  return (
    <Container>
      <LoadingBackdrop
        text="Alterando para"
        component={selectedCompany?.name}
        loading={loading}
      />
      <Header />

      <Content>
        <Outlet />
        <Dialog open={isModal}>
          <Page hasCaptcha={hasCaptcha === 'true'}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              {user?.avatar_url ? (
                <Avatar sx={{ width: '76px', height: '76px' }}>
                  <img
                    src={user?.avatar_url as string}
                    alt="Foto de perfil"
                    style={{ width: '100%' }}
                  />
                </Avatar>
              ) : (
                <Avatar
                  {...stringAvatar(user?.name as string)}
                  sx={{
                    width: '76px',
                    height: '76px',
                    fontSize: '24px',
                    bgcolor: '#12094d',
                  }}
                />
              )}

              <DialogTitleStyles>
                {`Reconectar como ${user?.name} `}
              </DialogTitleStyles>
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {loadingModal ? (
                  <CircularProgress sx={{ marginTop: '10px' }} />
                ) : (
                  <form
                    onSubmit={handleSubmit(reAuth)}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <InputPassword
                      control={control}
                      label="digite sua senha"
                      name="password"
                      required
                      autoComplete="current-password"
                      style={{ width: '360px', height: '56px' }}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '20px',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginBottom: '10px',
                      }}
                    />

                    <TurnstileWidget
                      control={control}
                      name="token"
                      turnstileRef={turnstileRef}
                    />

                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      gap="10px"
                      alignItems="center"
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        style={{ width: '360px', height: '56px' }}
                        disabled={loadingModal || btnIsDisabledByCaptcha}
                      >
                        Entrar
                      </Button>
                      <Button
                        variant="text"
                        onClick={handleSignOut}
                        style={{
                          width: 'fit-content',
                          height: '46px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          textDecorationLine: 'underline',
                          whiteSpace: 'nowrap',
                          textTransform: 'none',
                          fontSize: '15px',
                        }}
                      >
                        Sair do portal
                      </Button>
                    </Box>
                  </form>
                )}
              </Box>
              <Divider />
              <DialogTextStyles>
                Por questões de segurança, você foi desconectado por ficar 20
                minutos sem atividade.
              </DialogTextStyles>
            </Box>
          </Page>
        </Dialog>
      </Content>
    </Container>
  );
}
